/**
 * Previews — lazy, in the worker (DI-2-1 §2.5).
 *
 * `createImageBitmap(file, { resizeWidth | resizeHeight, resizeQuality })` →
 * `OffscreenCanvas` → `convertToBlob({ type: "image/jpeg", quality: 0.8 })`.
 * Never eagerly for the whole set: the guided viewer (DI-2-9) and the preview
 * upload (M3) call it per image.
 *
 * A file that does not end with EOI (every committed 64 KiB head) yields
 * `blob: null` + `preview_unavailable` — never a throw, and never a
 * half-decoded grey image. jsdom has neither `createImageBitmap` nor
 * `OffscreenCanvas`, so the unit test covers the failure path; the journey
 * decodes `tiny.jpg` in Chromium.
 */

import type { ValidityIssue } from "./types.js";
import { issue } from "./parseFile.js";
import { EOI_WINDOW, hasEoiNearEnd, walkHead } from "./jpeg/segments.js";
import { readExif } from "./jpeg/exif.js";
import { embeddedThumbnail } from "./jpeg/thumbnail.js";

export const PREVIEW_LONG_EDGE = 1600;
/**
 * DI-3-S41 §2.4 — up to this long edge a tile is served from the thumbnail
 * the camera embedded (EXIF IFD1, 160 px on the M350), when it has one and it
 * is at least `EMBEDDED_MIN_RATIO` of the size asked: no full decode.
 */
export const EMBEDDED_MAX_LONG_EDGE = 320;
const EMBEDDED_MIN_RATIO = 0.5;
/** The head read for the embedded thumbnail (the parse reads the same 64 KiB). */
const THUMB_HEAD_BYTES = 64 * 1024;

export interface PreviewResult {
  blob: Blob | null;
  width?: number;
  height?: number;
  issue?: ValidityIssue;
}

function unavailable(detail: string): PreviewResult {
  return { blob: null, issue: issue("preview_unavailable", detail) };
}

async function embeddedPart(
  file: Blob,
  longEdge: number,
): Promise<PreviewResult | null> {
  const head = new Uint8Array(
    await file.slice(0, Math.min(file.size, THUMB_HEAD_BYTES)).arrayBuffer(),
  );
  const t = embeddedThumbnail(head);
  if (!t || Math.max(t.width, t.height) < longEdge * EMBEDDED_MIN_RATIO)
    return null;
  return {
    blob: file.slice(t.start, t.end, "image/jpeg"),
    width: t.width,
    height: t.height,
  };
}

/**
 * DI-3-S41 §2.4 — the tile path without the worker: two small reads (the
 * head, the last 4 KiB) and a byte cut, cheap enough for the page thread, so
 * a tile never queues behind the hashing. Null = no usable embedded
 * thumbnail (or a cut file) — ask the worker (`makePreview`).
 */
export async function embeddedPreview(
  file: Blob,
  longEdge: number,
): Promise<PreviewResult | null> {
  if (longEdge > EMBEDDED_MAX_LONG_EDGE || file.size < 4) return null;
  const end = new Uint8Array(
    await file.slice(Math.max(0, file.size - EOI_WINDOW)).arrayBuffer(),
  );
  if (!hasEoiNearEnd(end)) return null;
  return embeddedPart(file, longEdge);
}

/**
 * `dims` (from the parsed head) lets a 60 MP file be decoded ONCE, straight to
 * the preview size; without it the file is decoded, measured and re-decoded.
 */
export async function makePreview(
  file: Blob,
  longEdge: number = PREVIEW_LONG_EDGE,
  dims?: { width: number; height: number },
): Promise<PreviewResult> {
  if (file.size < 4) return unavailable("file too small");
  const end = new Uint8Array(
    await file.slice(Math.max(0, file.size - EOI_WINDOW)).arrayBuffer(),
  );
  if (!hasEoiNearEnd(end)) return unavailable("the file ends before EOI");
  if (longEdge <= EMBEDDED_MAX_LONG_EDGE) {
    const embedded = await embeddedPart(file, longEdge);
    if (embedded) return embedded;
  }
  if (
    typeof createImageBitmap !== "function" ||
    typeof OffscreenCanvas === "undefined"
  )
    return unavailable("this browser cannot decode off the main thread");

  let bitmap: ImageBitmap | null = null;
  try {
    let width = dims?.width;
    let height = dims?.height;
    if (width === undefined || height === undefined) {
      bitmap = await createImageBitmap(file);
      width = bitmap.width;
      height = bitmap.height;
    }
    const scale = Math.min(1, longEdge / Math.max(width, height));
    const w = Math.max(1, Math.round(width * scale));
    const h = Math.max(1, Math.round(height * scale));
    if (!bitmap || scale < 1) {
      bitmap?.close();
      bitmap = await createImageBitmap(file, {
        resizeWidth: w,
        resizeHeight: h,
        resizeQuality: "medium",
      });
    }
    const canvas = new OffscreenCanvas(w, h);
    const ctx = canvas.getContext("2d");
    if (!ctx) return unavailable("no 2d context");
    ctx.drawImage(bitmap, 0, 0, w, h);
    const blob = await canvas.convertToBlob({
      type: "image/jpeg",
      quality: 0.8,
    });
    return { blob, width: w, height: h };
  } catch (e) {
    return unavailable(e instanceof Error ? e.message : String(e));
  } finally {
    bitmap?.close();
  }
}

/** EXIF Orientation 5–8: the picture is shown turned by 90°. */
const TAG_ORIENTATION = "Orientation";

/**
 * DI-3-S58 §2.2 — the frame size from the file's head: the first SOF marker
 * (SOF0 baseline / SOF2 progressive, `walkHead`) inside the same 64 KiB the
 * EXIF and the embedded thumbnail are read from. As the bitmap will be
 * (EXIF orientation 5–8 swaps the two). Undefined when the head carries no
 * SOF (an M4T visual frame's 53 KB APP7 pushes it past 64 KiB): the caller
 * then decodes to measure, as before.
 */
export async function frameSize(
  file: Blob,
): Promise<{ width: number; height: number } | undefined> {
  const head = new Uint8Array(
    await file.slice(0, Math.min(file.size, THUMB_HEAD_BYTES)).arrayBuffer(),
  );
  let j;
  try {
    j = walkHead(head);
  } catch {
    return undefined;
  }
  const sof = j.sof;
  if (!sof || sof.width <= 0 || sof.height <= 0) return undefined;
  let turned = false;
  if (j.exif) {
    const r = readExif(
      head.subarray(j.exif.payloadStart + 6, j.exif.payloadEnd),
    );
    const o = r?.tags[TAG_ORIENTATION];
    turned = typeof o === "number" && o >= 5 && o <= 8;
  }
  return turned
    ? { width: sof.height, height: sof.width }
    : { width: sof.width, height: sof.height };
}

/**
 * DI-3-S58 §2.2 — `makePreview` for a file with no parsed row (a folder
 * source): above the embedded size the frame size comes from the head, so the
 * original is decoded ONCE, straight to the preview size.
 */
export async function framedPreview(
  file: Blob,
  longEdge: number = PREVIEW_LONG_EDGE,
): Promise<PreviewResult> {
  const dims =
    longEdge > EMBEDDED_MAX_LONG_EDGE && file.size >= 4
      ? await frameSize(file)
      : undefined;
  return makePreview(file, longEdge, dims);
}
